/**
 * 指挥端外发指令位置点（对标 Locus ActionDisplayPoints）
 * 与队员上报「报」点区分：蓝/红 SOS 图标 + 指令文字
 */
import mapboxgl from 'mapbox-gl';
import { toMapLngLat, getActiveBasemapIdForCoord } from './basemapCoord';
import apiFetch from '../apiFetch';

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function readProjectionName(map) {
  try {
    return map?.getProjection?.()?.name || 'mercator';
  } catch (_) {
    return 'mercator';
  }
}

function formatCoordNum(value, digits = 6) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n.toFixed(digits);
}

/**
 * 指令点图标：蓝/红 SOS 圆标 + 上方指令文字（对标 Locus ActionDisplayPoints）
 */
function createCommandPinElement(pin) {
  const important = !!pin.important;
  const el = document.createElement('div');
  el.className = 'zhxt-command-pin';
  el.title = `指令点${important ? '（重要）' : ''}`;
  el.style.cssText = [
    'display:flex',
    'flex-direction:column',
    'align-items:center',
    'cursor:pointer',
    'pointer-events:auto',
    'max-width:180px',
    'line-height:0'
  ].join(';');

  const label = document.createElement('div');
  label.className = 'zhxt-command-pin-label';
  label.style.cssText = [
    'padding:5px 8px',
    'margin-bottom:4px',
    important ? 'background:rgba(254,226,226,.94)' : 'background:rgba(219,234,254,.94)',
    important ? 'border:1px solid #f87171' : 'border:1px solid #60a5fa',
    'border-radius:4px',
    'box-shadow:0 1px 6px rgba(0,0,0,.16)',
    'font-size:11px',
    'line-height:1.35',
    'color:#1e293b',
    'white-space:normal',
    'word-break:break-word',
    'text-align:left'
  ].join(';');
  const titleColor = important ? '#b91c1c' : '#1d4ed8';
  const who = escapeHtml(pin.created_by_label || pin.created_by_name || '指挥');
  const text = escapeHtml(String(pin.text || '').slice(0, 100));
  label.innerHTML = `<div style="font-weight:700;color:${titleColor};margin-bottom:2px">${who} · 指令</div>`
    + `<div>${text}</div>`;

  const fill = important ? '#dc2626' : '#2563eb';
  const stroke = important ? '#991b1b' : '#1e40af';
  const icon = document.createElement('div');
  icon.style.cssText = 'width:36px;height:36px;margin:0;padding:0;line-height:0';
  icon.innerHTML = `
    <svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg" style="display:block;pointer-events:none;filter:drop-shadow(0 1px 2px rgba(0,0,0,.35))">
      <circle cx="18" cy="18" r="15" fill="${fill}" stroke="${stroke}" stroke-width="2"/>
      <circle cx="18" cy="18" r="11" fill="none" stroke="#fff" stroke-width="1.5" opacity="0.9"/>
      <text x="18" y="22" text-anchor="middle" fill="#fff" font-size="9" font-weight="800"
        font-family="system-ui,sans-serif" letter-spacing="0.5">SOS</text>
    </svg>
  `;

  el.appendChild(label);
  el.appendChild(icon);
  return el;
}

function buildCommandPinPopupHtml(pin) {
  const when = pin.created_at ? new Date(pin.created_at).toLocaleString() : '';
  const lat = formatCoordNum(pin.latitude);
  const lng = formatCoordNum(pin.longitude);
  const who = escapeHtml(pin.created_by_label || pin.created_by_name || '指挥');
  return `
    <div style="width:max-content;max-width:220px;font-size:11px;line-height:1.35">
      <div style="font-weight:700;margin-bottom:2px;color:${pin.important ? '#b91c1c' : '#1d4ed8'}">
        ${who} · 外发指令点${pin.important ? '（重要）' : ''}
      </div>
      <div style="white-space:pre-wrap;word-break:break-word;margin-bottom:3px">${escapeHtml(pin.text || '')}</div>
      <div style="color:#334155;margin-bottom:2px;font-family:ui-monospace,Menlo,Consolas,monospace;font-size:10px;line-height:1.3">
        <div>纬度：${lat != null ? escapeHtml(lat) : '—'}</div>
        <div>经度：${lng != null ? escapeHtml(lng) : '—'}</div>
      </div>
      <div style="color:#64748b;margin-bottom:4px;font-size:10px">${escapeHtml(when)}</div>
      <div style="display:flex;gap:4px;justify-content:flex-end">
        <button type="button" data-zhxt-cmd-pin-hide="${escapeHtml(pin.id)}"
          style="padding:1px 6px;font-size:10px;line-height:1.3;border:1px solid #64748b;background:#fff;color:#334155;border-radius:3px;cursor:pointer;white-space:nowrap">
          永久从地图移除
        </button>
      </div>
    </div>
  `;
}

/**
 * @param {Map} pinsMap Map<id, { marker, pin }>
 */
export function upsertCommandPinOnMap(map, pin, pinsMap, options = {}) {
  if (!map || !pin?.id || !pinsMap) return null;
  const projection = readProjectionName(map);
  if (projection === 'globe') return null;

  const lng = Number(pin.longitude);
  const lat = Number(pin.latitude);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;

  const existing = pinsMap.get(pin.id);
  if (existing?.marker) {
    try { existing.marker.remove(); } catch (_) { /* ignore */ }
    pinsMap.delete(pin.id);
  }

  const display = toMapLngLat(lng, lat, getActiveBasemapIdForCoord(map), map) || [lng, lat];
  const el = createCommandPinElement(pin);
  const popup = new mapboxgl.Popup({
    offset: 22,
    maxWidth: '240px',
    closeButton: true,
    className: 'zhxt-command-pin-popup',
    anchor: 'bottom'
  }).setHTML(buildCommandPinPopupHtml(pin));

  const marker = new mapboxgl.Marker({
    element: el,
    anchor: 'bottom',
    pitchAlignment: 'viewport',
    rotationAlignment: 'viewport',
    offset: [-14, 0]
  })
    .setLngLat(display)
    .setPopup(popup)
    .addTo(map);

  const onPopupClick = (event) => {
    const btn = event.target?.closest?.('[data-zhxt-cmd-pin-hide]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    const id = btn.getAttribute('data-zhxt-cmd-pin-hide');
    if (!id) return;
    removeCommandPinFromMap(pinsMap, id);
    options.onHidden?.(id);
  };

  const onPopupOpen = () => {
    el.classList.add('is-popup-open');
    if (el.parentElement) el.parentElement.style.zIndex = '1';
    const popupEl = popup.getElement();
    if (popupEl) {
      popupEl.style.zIndex = '40';
      popupEl.addEventListener('click', onPopupClick);
    }
  };
  const onPopupClose = () => {
    el.classList.remove('is-popup-open');
    if (el.parentElement) el.parentElement.style.zIndex = '';
    popup.getElement()?.removeEventListener('click', onPopupClick);
  };
  popup.on('open', onPopupOpen);
  popup.on('close', onPopupClose);

  pinsMap.set(pin.id, { marker, pin, popup });
  return pinsMap.get(pin.id);
}

export function removeCommandPinFromMap(pinsMap, id) {
  if (!pinsMap || !id) return;
  const item = pinsMap.get(id);
  if (!item) return;
  try { item.marker?.remove(); } catch (_) { /* ignore */ }
  pinsMap.delete(id);
}

export function reprojectCommandPins(map, pinsMap, basemapId) {
  if (!map || !pinsMap) return;
  pinsMap.forEach((item) => {
    const pin = item?.pin;
    if (!pin || !item.marker) return;
    const display = toMapLngLat(
      Number(pin.longitude),
      Number(pin.latitude),
      basemapId || getActiveBasemapIdForCoord(map),
      map
    );
    if (display) item.marker.setLngLat(display);
  });
}

export function clearAllCommandPins(pinsMap) {
  if (!pinsMap) return;
  pinsMap.forEach((item) => {
    try { item.marker?.remove(); } catch (_) { /* ignore */ }
  });
  pinsMap.clear();
}

export async function fetchActiveCommandPins(fetchFn = apiFetch) {
  const response = await fetchFn('/api/tracker/command-pins');
  const raw = await response.text();
  let data = {};
  try { data = raw ? JSON.parse(raw) : {}; } catch (_) {
    throw new Error('获取指令位置点失败：返回非 JSON（请确认后端已重启）');
  }
  if (!response.ok || !data.success) {
    throw new Error(data.error || '获取指令位置点失败');
  }
  return data.pins || [];
}

/** 从 WS / 外发成功回包规范成地图 pin */
export function normalizeCommandPinPayload(payload) {
  if (!payload) return null;
  const lat = payload.latitude != null ? Number(payload.latitude) : NaN;
  const lng = payload.longitude != null ? Number(payload.longitude) : NaN;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const id = payload.batch_id || payload.id;
  if (!id) return null;
  return {
    id: String(id),
    batch_id: payload.batch_id || String(id),
    text: payload.text || '',
    important: !!payload.important,
    latitude: lat,
    longitude: lng,
    command_kind: payload.command_kind || 'location',
    created_by: payload.created_by,
    created_by_name: payload.created_by_name || payload.created_by_label || '',
    created_by_label: payload.created_by_label || payload.created_by_name || '',
    created_at: payload.created_at || new Date().toISOString(),
    pin_on_map: true
  };
}

/** 约 1.2e-4 度 ≈ 13m，用于救援目标与指令点同址去重 */
export function coordsNear(lat1, lng1, lat2, lng2, eps = 1.2e-4) {
  const a = Number(lat1);
  const b = Number(lng1);
  const c = Number(lat2);
  const d = Number(lng2);
  if (![a, b, c, d].every(Number.isFinite)) return false;
  return Math.abs(a - c) <= eps && Math.abs(b - d) <= eps;
}

export function commandPinCoversLatLng(pinsMap, lat, lng, eps = 1.2e-4) {
  if (!pinsMap || typeof pinsMap.values !== 'function') return false;
  for (const item of pinsMap.values()) {
    const pin = item?.pin;
    if (!pin) continue;
    if (coordsNear(pin.latitude, pin.longitude, lat, lng, eps)) return true;
  }
  return false;
}

/**
 * 指令点已覆盖同坐标时，从指挥地图移除救援目标标记（保留列表数据由调用方决定）
 * @returns {string[]} 被移除的 rescue target id
 */
export function removeRescueMarkersCoveredByCommandPins(rescueMarkersRef, pinsMap, eps = 1.2e-4) {
  const removed = [];
  const list = rescueMarkersRef?.current;
  if (!Array.isArray(list) || !pinsMap) return removed;
  const keep = [];
  list.forEach((entry) => {
    const data = entry?.data;
    const lat = data?.lat;
    const lng = data?.lng;
    if (data && commandPinCoversLatLng(pinsMap, lat, lng, eps)) {
      try { entry.marker?.remove?.(); } catch (_) { /* ignore */ }
      const tid = data.id != null ? String(data.id) : null;
      if (tid) removed.push(tid);
      return;
    }
    keep.push(entry);
  });
  rescueMarkersRef.current = keep;
  return removed;
}
